import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useOrderLineItems, useCancelPurchaseRequisitionLineItem } from '@/hooks';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';
import { getErrorMessage } from '@/utils/httpClient';
import { useTableSort } from '@/hooks/useTableSort';
import { SortableTableCell } from '@/components/shared/SortableTableCell';

export interface EditPurchaseRequisitionDetails {
  id: string;
  identifier?: string;
  status?: string;
  orderDate?: string;
  logFormNumber?: number | string;
}

interface EditPurchaseRequisitionModalProps {
  open: boolean;
  onClose: () => void;
  order?: EditPurchaseRequisitionDetails | null;
}

interface EditableLineItem {
  id: string;
  name: string;
  lin?: string;
  nsn?: string;
  orderedQuantity: number;
  receivedQuantity: number;
  outstandingQuantity: number;
}

const EditLineItemsTable: React.FC<{
  items: EditableLineItem[];
  onAdjust: (item: EditableLineItem) => void;
}> = ({ items, onAdjust }) => {
  const { sortField, sortDirection, onSort, sortedRows } = useTableSort(
    items as unknown as Record<string, unknown>[],
  );
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <SortableTableCell field="name" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Item</SortableTableCell>
          <SortableTableCell field="lin" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>LIN</SortableTableCell>
          <SortableTableCell field="nsn" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>NSN</SortableTableCell>
          <SortableTableCell field="orderedQuantity" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="right">Ordered</SortableTableCell>
          <SortableTableCell field="receivedQuantity" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="right">Received</SortableTableCell>
          <SortableTableCell field="outstandingQuantity" sortField={sortField} sortDirection={sortDirection} onSort={onSort} align="right">Outstanding</SortableTableCell>
          <TableCell align="right" sx={{ fontWeight: 600 }}>Action</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {(sortedRows as unknown as EditableLineItem[]).map((item) => (
          <TableRow key={item.id}>
            <TableCell>{item.name}</TableCell>
            <TableCell>{item.lin ?? '—'}</TableCell>
            <TableCell>{item.nsn ?? '—'}</TableCell>
            <TableCell align="right">{item.orderedQuantity}</TableCell>
            <TableCell align="right">{item.receivedQuantity}</TableCell>
            <TableCell align="right">{item.outstandingQuantity}</TableCell>
            <TableCell align="right">
              <Button
                size="small"
                variant="outlined"
                onClick={() => onAdjust(item)}
              >
                Adjust
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export const EditPurchaseRequisitionModal = ({
  open,
  onClose,
  order,
}: EditPurchaseRequisitionModalProps) => {
  const [displayOrder, setDisplayOrder] = useState<EditPurchaseRequisitionDetails | null>(
    order ?? null,
  );
  const lineItemsQuery = useOrderLineItems(displayOrder?.id);
  const cancelLineItem = useCancelPurchaseRequisitionLineItem();
  const [selectedLineItem, setSelectedLineItem] = useState<EditableLineItem | null>(null);
  const [quantityToCancel, setQuantityToCancel] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const items = useMemo<EditableLineItem[]>(() => {
    if (!lineItemsQuery.data) {
      return [];
    }

    return lineItemsQuery.data.flatMap((item) => {
      const ordered = item.entity_data.quantity ?? 0;
      const received =
        item.receipts?.reduce(
          (sum, receipt) => sum + (receipt.entity_data?.quantity_received ?? 0),
          0,
        ) ?? 0;
      const outstanding = Math.max(0, ordered - received);

      if (outstanding === 0) {
        return [];
      }

      return [
        {
          id: item.id,
          name: item.catalog?.entity_data?.nomenclature ?? item.catalog?.display_name ?? item.display_name ?? 'Unknown Item',
          lin: item.catalog?.entity_data?.lin,
          nsn: item.catalog?.entity_data?.nsn,
          orderedQuantity: ordered,
          receivedQuantity: received,
          outstandingQuantity: outstanding,
        },
      ];
    });
  }, [lineItemsQuery.data]);

  useEffect(() => {
    if (order) {
      setDisplayOrder(order);
    }
  }, [order]);

  useEffect(() => {
    if (!open) {
      setSelectedLineItem(null);
      setQuantityToCancel('');
      setCancellationReason('');
      setSubmitError(null);
    }
  }, [open]);

  const handleOpenAdjustDialog = (lineItem: EditableLineItem) => {
    setSelectedLineItem(lineItem);
    setQuantityToCancel(String(lineItem.outstandingQuantity));
    setCancellationReason('');
    setSubmitError(null);
  };

  const handleCloseAdjustDialog = () => {
    setSelectedLineItem(null);
    setQuantityToCancel('');
    setCancellationReason('');
    setSubmitError(null);
  };

  const parsedQuantityToCancel = Number.parseInt(quantityToCancel, 10);
  const quantityIsValid =
    selectedLineItem !== null &&
    Number.isInteger(parsedQuantityToCancel) &&
    parsedQuantityToCancel >= 1 &&
    parsedQuantityToCancel <= selectedLineItem.outstandingQuantity;
  const canSubmit =
    Boolean(displayOrder?.id) &&
    Boolean(selectedLineItem) &&
    quantityIsValid &&
    cancellationReason.trim().length > 0;

  const handleSubmitAdjustment = async () => {
    if (!displayOrder?.id || !selectedLineItem || !quantityIsValid) {
      return;
    }

    setSubmitError(null);

    try {
      const response = await cancelLineItem.mutateAsync({
        orderId: displayOrder.id,
        lineItemId: selectedLineItem.id,
        quantityToCancel: parsedQuantityToCancel,
        cancellationReason: cancellationReason.trim(),
      });

      handleCloseAdjustDialog();
      if (response.status === 'Cancelled') {
        onClose();
      }
    } catch (error) {
      setSubmitError(getErrorMessage(error));
    }
  };

  const title = displayOrder?.logFormNumber
    ? `Edit Purchase Requisition ${String(displayOrder.logFormNumber).padStart(6, '0')}`
    : 'Edit Purchase Requisition';

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        TransitionProps={{
          onExited: () => {
            setDisplayOrder(null);
          },
        }}
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          {lineItemsQuery.isLoading ? (
            <Typography>Loading line items...</Typography>
          ) : lineItemsQuery.isError ? (
            <Alert severity="error">Failed to load purchase requisition line items.</Alert>
          ) : items.length === 0 ? (
            <Typography>All line items on this purchase requisition have been fully received. There are no outstanding quantities to adjust.</Typography>
          ) : (
            <EditLineItemsTable items={items} onAdjust={handleOpenAdjustDialog} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(selectedLineItem)}
        onClose={handleCloseAdjustDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Adjust Line Item</DialogTitle>
        <DialogContent>
          {selectedLineItem && (
            <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {selectedLineItem.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Outstanding quantity: {selectedLineItem.outstandingQuantity}
              </Typography>
              <TextField
                label="Quantity to Cancel"
                type="number"
                value={quantityToCancel}
                onChange={(event) => setQuantityToCancel(event.target.value)}
                inputProps={{ min: 1, max: selectedLineItem.outstandingQuantity }}
              />
              <TextField
                label="Cancellation Reason"
                value={cancellationReason}
                onChange={(event) => setCancellationReason(event.target.value)}
                multiline
                minRows={3}
              />
              {submitError && <Alert severity="error">{submitError}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseAdjustDialog}
            color="inherit"
            disabled={cancelLineItem.isPending}
          >
            Close
          </Button>
          <ViewerGuardedButton
            onClick={handleSubmitAdjustment}
            variant="contained"
            disabled={!canSubmit || cancelLineItem.isPending}
          >
            {cancelLineItem.isPending ? 'Updating...' : 'Update'}
          </ViewerGuardedButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
